import HistoryIcon from '@mui/icons-material/History'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CommandHistoryDialog from './CommandHistoryDialog'
import CommandRecentExecutionsTable from './CommandRecentExecutionsTable'

interface AdminCommandCardProps {
  title: string
  description: string
  actionLabel: string
  confirmationMessage: string
  onExecute: () => Promise<void>
  isPending: boolean
  commandType: DevToolsCommandType
}

const AdminCommandCard = ({
  title,
  description,
  actionLabel,
  confirmationMessage,
  onExecute,
  isPending,
  commandType,
}: AdminCommandCardProps) => {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)

  const handleOpenDialog = () => setIsDialogOpen(true)
  const handleCloseDialog = () => {
    if (!isPending) setIsDialogOpen(false)
  }

  const handleConfirm = async () => {
    try {
      await onExecute()
    } finally {
      setIsDialogOpen(false)
    }
  }

  return (
    <>
      <Card
        elevation={3}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
        }}
      >
        <CardHeader
          avatar={<WarningAmberIcon color="warning" />}
          title={<Typography variant="h6">{title}</Typography>}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <Button
            variant="contained"
            color="warning"
            onClick={handleOpenDialog}
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {actionLabel}
          </Button>
        </CardActions>

        <Divider />

        <Stack spacing={1} sx={{ px: 2, py: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('admin.devTools.history.recentTitle')}
          </Typography>
          <CommandRecentExecutionsTable commandType={commandType} />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="text"
              startIcon={<HistoryIcon />}
              onClick={() => setIsHistoryDialogOpen(true)}
            >
              {t('admin.devTools.history.viewFullHistory')}
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.devTools.dialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">{confirmationMessage}</Alert>
            <DialogContentText>{t('admin.devTools.dialog.prompt')}</DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isPending}>
            {t('admin.devTools.dialog.cancel')}
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            color="error"
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('admin.devTools.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <CommandHistoryDialog
        open={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
        commandType={commandType}
        commandTitle={title}
      />
    </>
  )
}

export default AdminCommandCard
